import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import {
  HttpService, GlobalQueryParameter, RestApiModelInterface,
  Login, Mitarbeiter, Stundeninfo, Stunden
} from '@proffix/restapi-angular-library';

import { AbwesenheitsAntrag, AbwesenheitsAntragStunden, StundenAntrag, StundenPlan, StundeninfoCache, Feiertag } from './';

@Injectable()
export class CustomProffixService {

  constructor(private httpService: HttpService) { }

  /**
   * Gibt alle Stundeninfos zurück
   * @param params Object mit den Parametern welche dem Request mitgegeben werden sollen
   */
  public getAllStundeninfo(params: any): Observable<Stundeninfo> {
    return this.httpService.get<Stundeninfo>('ZEI/Stundeninfo', params);
  }

  /**
   * Gibt alle Stundenpläne zurück
   * @param params Object mit den Parametern welche dem Request mitgegeben werden sollen
   */
  public getAllStundenPlaene(params: any): Observable<StundenPlan[]> {
    return this.httpService.get<StundenPlan[]>('PRO/Zusatztabelle/PRO_StdPlan', params);
  }

  /**
   * Gibt alle Feiertage zurück
   * @param params Object mit den Parametern welche dem Request mitgegeben werden sollen
   */
  public getAllFeiertage(params: any): Observable<Feiertag[]> {
    return this.httpService.get<Feiertag[]>('PRO/Zusatztabelle/PRO_Feiertage', params);
  }

  /**
   * Falls eingeloggt. Gibt das aktuelle Login-Objekt zurück.
   */
  public getLogin(): Observable<Login> {
    return this.httpService.get<Login>('PRO/Login');
  }

  public putStunden(data: Stunden): Observable<void> {
    return this.httpService.put('ZEI/Stunden/' + data.StundenNr, data);
  }

  public postStunden(data: Stunden): Observable<string> {
    return this.httpService.post('ZEI/Stunden', data);
  }

  public deleteStunden(data: Stunden): Observable<void> {
    return this.httpService.delete('ZEI/Stunden/' + data.StundenNr);
  }

  public getListOfAssignedMitarbeiter(mitarbeiterNr: number = null, isManager: boolean = false): Observable<Mitarbeiter[]> {
    var filter = '';

    if(mitarbeiterNr && isManager == false) {
      filter = 'MitarbeiterNr==' + mitarbeiterNr + '|' + 'Z_Zeitmanagement_Abteilungsleiter==' + mitarbeiterNr;
    }

    return this.httpService.get<Mitarbeiter[]>('PRO/Mitarbeiter', {
      sort: 'Name',
      limit: 1000,
      filter: filter,
    }
    );
  }

  /*** Stunden Anträge ***/

  public getStundenAntrag(params: any): Observable<StundenAntrag[]> {
    return this.httpService.get<StundenAntrag[]>('PRO/Zusatztabelle/ZUS_TM_StundenAntraege', params);
  }

  public postStundenAntrag(data: StundenAntrag): Observable<string> {
    const copy = this.prepareStundenAntragForApiRequest(data);

    return this.httpService.post('PRO/Zusatztabelle/ZUS_TM_StundenAntraege', copy);
  }

  public putStundenAntrag(data: StundenAntrag): Observable<void> {
    const copy = this.prepareStundenAntragForApiRequest(data);

    return this.httpService.put('PRO/Zusatztabelle/ZUS_TM_StundenAntraege/' + copy.Id, copy);
  }

  private prepareStundenAntragForApiRequest(data: StundenAntrag): StundenAntrag {
    // remove properties the RESP API doesn't know
    const copy: StundenAntrag = (JSON.parse(JSON.stringify(data)));
    delete copy.OriginalStunden;

    if (!copy.StartZeit) { copy.StartZeit = ''; }
    if (!copy.EndZeit) { copy.EndZeit = ''; }
    if (!copy.BemerkungManager) { copy.BemerkungManager = ''; }
    if (!copy.BemerkungMitarbeiter) { copy.BemerkungMitarbeiter = ''; }

    return copy;
  }

  /*** Abwesenheits-Anträge (Kopf) ***/

  public getAbwesenheitsAntraege(params: any): Observable<AbwesenheitsAntrag[]> {
    return this.httpService.get<AbwesenheitsAntrag[]>('PRO/Zusatztabelle/ZUS_TM_AbwAntraege', params);
  }

  public postAbwesenheitsAntraege(data: AbwesenheitsAntrag): Observable<string> {
    return this.httpService.post('PRO/Zusatztabelle/ZUS_TM_AbwAntraege', data);
  }

  public putAbwesenheitsAntraege(data: AbwesenheitsAntrag): Observable<void> {
    if (!data.BemerkungManager) { data.BemerkungManager = ''; }
    if (!data.BemerkungMitarbeiter) { data.BemerkungMitarbeiter = ''; }

    return this.httpService.put('PRO/Zusatztabelle/ZUS_TM_AbwAntraege/' + data.Id, data);
  }

  /*** Stunden zu Abwesenheits-Anträge (Details) ***/

  public getAbwesenheitsAntragStunden(params: any): Observable<AbwesenheitsAntragStunden[]> {
    return this.httpService.get<AbwesenheitsAntragStunden[]>('PRO/Zusatztabelle/ZUS_TM_AbwAntraegeStd', params);
  }

  public postAbwesenheitsAntragStunden(data: AbwesenheitsAntragStunden): Observable<string> {
    return this.httpService.post('PRO/Zusatztabelle/ZUS_TM_AbwAntraegeStd', data);
  }

  public putAbwesenheitsAntragStunden(data: AbwesenheitsAntragStunden): Observable<void> {
    return this.httpService.put('PRO/Zusatztabelle/ZUS_TM_AbwAntraegeStd/' + data.Id, data);
  }

  /*** Stundeninfo Cache ***/

  public getStundeninfoCache(params: any): Observable<StundeninfoCache[]> {
    return this.httpService.get<StundeninfoCache[]>('PRO/Zusatztabelle/ZUS_Stundeninfo_Cache', params);
  }

  public postStundeninfoCache(data: StundeninfoCache): Observable<string> {
    return this.httpService.post('PRO/Zusatztabelle/ZUS_Stundeninfo_Cache', data);
  }

  public putStundeninfoCache(data: StundeninfoCache): Observable<void> {
    return this.httpService.put('PRO/Zusatztabelle/ZUS_Stundeninfo_Cache/' + data.MitNrPRO, data);
  }

}



// WEBPACK FOOTER //
// C:/.NET Projects/Angular Apps/TimeManagement/src/app/services/custom-proffix.service.ts